import { StrictMode } from 'react';
import { createRoot } from 'react-dom/client';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { TonConnectUIProvider } from '@tonconnect/ui-react';
import { BrowserRouter } from 'react-router-dom';
import '@tb/ui/styles.css';
import './styles.css';
import { App } from './App';
import { RuntimeConfigProvider } from './RuntimeConfigProvider';
import { SessionProvider } from './auth/SessionProvider';
import { WalletProvider } from './auth/WalletProvider';
import { fetchRuntimeConfig, type RuntimeConfig } from './config';
import { LocaleProvider } from './i18n/LocaleProvider';
import { initTelegram } from './telegram/telegram';
import { BootError, BootSplash } from './components/Boot';
import { ErrorBoundary } from './components/ErrorBoundary';

initTelegram();

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      retry: 1,
      staleTime: 15_000,
      refetchOnWindowFocus: false,
    },
  },
});

const container = document.getElementById('root');
if (!container) throw new Error('#root is missing from index.html');

const root = createRoot(container);
root.render(<BootSplash />);

void fetchRuntimeConfig().then(
  (config: RuntimeConfig) => {
    root.render(
      <StrictMode>
        <ErrorBoundary>
          <RuntimeConfigProvider config={config}>
            <QueryClientProvider client={queryClient}>
              <TonConnectUIProvider
                manifestUrl={config.manifestUrl}
                uiPreferences={{ theme: 'SYSTEM' }}
                actionsConfiguration={{
                  twaReturnUrl: config.botUrl as `${string}://${string}`,
                }}
              >
                <SessionProvider>
                  <LocaleProvider>
                    <WalletProvider>
                      <BrowserRouter>
                        <App />
                      </BrowserRouter>
                    </WalletProvider>
                  </LocaleProvider>
                </SessionProvider>
              </TonConnectUIProvider>
            </QueryClientProvider>
          </RuntimeConfigProvider>
        </ErrorBoundary>
      </StrictMode>,
    );
  },
  (error: unknown) => {
    root.render(<BootError message={error instanceof Error ? error.message : String(error)} />);
  },
);
